// =============================================================================
// Dashboard widget catalog.
//
// Three levels, mirroring the iOS widget gallery:
//   category  →  instance (which calendar / list / account)  →  variant + size
//
// A category is gated by the same feature id the sidebar uses, so a feature
// switched off in Settings disappears from the gallery (and its widgets stop
// rendering) automatically.
//
// This module is pure data + resolvers — no React, no body components (see
// registry.js for the variant → component mapping) so it can be imported
// from anywhere without cycles.
// =============================================================================

import {
  Calendar as CalendarIcon,
  Cake,
  CheckSquare,
  ShoppingCart,
  Wallet,
  PiggyBank,
  Dumbbell,
  Timer,
  BookHeart,
  Fuel,
  GraduationCap,
  FolderKanban
} from 'lucide-react';

// Grid geometry: 12 columns on desktop, 1 on phones; rows are 44px.
// Size presets are starting points — everything stays freely resizable.
const S = (w, h) => ({ w, h });

export const SIZE_LABELS = {
  s: { key: 'dashw.sizeSmall', fallback: 'Klein' },
  m: { key: 'dashw.sizeMedium', fallback: 'Mittel' },
  l: { key: 'dashw.sizeLarge', fallback: 'Groß' }
};

// ---------------------------------------------------------------------------
// Instance resolvers. Each returns [{ id, label?, labelKey?, fallback? }].
// Returning a single entry makes the gallery skip the instance step.
// ---------------------------------------------------------------------------
const calendarInstances = (data) => {
  const enabled = data?.settings?.enabledFeatures || {};
  const out = [];
  // The Settings toggle for the personal calendar flips BOTH
  // `showPersonalCalendar` and `enabledFeatures.calendar`, so it must gate
  // only the personal entry — shared calendars stay offerable on their own.
  const personalOn =
    enabled.calendar !== false && data?.settings?.showPersonalCalendar !== false;
  if (personalOn) {
    out.push({
      id: 'personal',
      labelKey: 'todo.personalCalendar',
      fallback: 'Persönlicher Kalender'
    });
  }
  for (const cal of data?.calendar?.sharedCalendars || []) {
    // Each shared calendar has its own sidebar toggle (`shared:<id>`).
    if (enabled[`shared:${cal.id}`] === false) continue;
    out.push({ id: cal.id, label: cal.name });
  }
  return out;
};

const todoInstances = (data) => [
  { id: 'all', labelKey: 'todo.allGroups', fallback: 'Alle Gruppen' },
  ...(data?.todo?.groups || []).map((g) => ({ id: g.id, label: g.name }))
];

const shoppingInstances = (data) => [
  { id: 'personal', labelKey: 'dashw.myList', fallback: 'Meine Liste' },
  ...(data?.shopping?.sharedLists || []).map((l) => ({ id: l.id, label: l.name }))
];

const savingsInstances = (data) => [
  { id: 'total', labelKey: 'dashw.allAccounts', fallback: 'Alle Konten' },
  ...(data?.budget?.savingsAccounts || []).map((a) => ({ id: a.id, label: a.name }))
];

const fitnessInstances = (data) => [
  { id: 'all', labelKey: 'dashw.allSports', fallback: 'Alle Sportarten' },
  ...(data?.fitness?.sports || [])
    .filter((s) => s && s.name)
    .map((s) => ({ id: s.name, label: s.name }))
];

// ---------------------------------------------------------------------------
// The catalog.
// ---------------------------------------------------------------------------
export const CATEGORIES = [
  {
    id: 'calendar',
    feature: 'calendar',
    icon: CalendarIcon,
    labelKey: 'nav.calendar',
    fallback: 'Kalender',
    instances: calendarInstances,
    // Offerable as long as ANY calendar is visible — turning the personal
    // calendar off in Settings must not take the shared ones with it.
    available: (data) => calendarInstances(data).length > 0,
    variants: [
      {
        id: 'calendar.day',
        labelKey: 'dashw.calendarDay',
        fallback: 'Tagesansicht',
        descKey: 'dashw.calendarDayDesc',
        descFallback: 'Termine von heute',
        min: S(3, 4),
        sizes: { s: S(4, 6), m: S(6, 8), l: S(12, 8) },
        defaultSize: 'm'
      },
      {
        id: 'calendar.week',
        labelKey: 'dashw.calendarWeek',
        fallback: 'Wochenansicht',
        descKey: 'dashw.calendarWeekDesc',
        descFallback: 'Die ganze Woche auf einen Blick',
        min: S(4, 4),
        sizes: { s: S(4, 4), m: S(6, 4), l: S(12, 4) },
        defaultSize: 'm'
      },
      {
        id: 'calendar.month',
        labelKey: 'dashw.calendarMonth',
        fallback: 'Monatsübersicht',
        descKey: 'dashw.calendarMonthDesc',
        descFallback: 'Ganzer Monat als Raster',
        min: S(4, 8),
        sizes: { m: S(6, 11), l: S(12, 12) },
        defaultSize: 'l'
      },
      {
        id: 'calendar.agenda',
        labelKey: 'dashw.calendarAgenda',
        fallback: 'Nächste Termine',
        descKey: 'dashw.calendarAgendaDesc',
        descFallback: 'Liste der kommenden Termine',
        min: S(3, 4),
        sizes: { s: S(3, 5), m: S(4, 8), l: S(6, 8) },
        defaultSize: 'm'
      }
    ]
  },
  {
    id: 'birthdays',
    feature: 'birthdays',
    icon: Cake,
    labelKey: 'nav.birthdays',
    fallback: 'Geburtstage',
    variants: [
      {
        id: 'birthdays.upcoming',
        labelKey: 'dashw.birthdaysUpcoming',
        fallback: 'Nächste Geburtstage',
        descKey: 'dashw.birthdaysUpcomingDesc',
        descFallback: 'Wer als Nächstes Geburtstag hat',
        min: S(3, 3),
        sizes: { s: S(3, 5), m: S(4, 7) },
        defaultSize: 'm'
      }
    ]
  },
  {
    id: 'todo',
    feature: 'todo',
    icon: CheckSquare,
    labelKey: 'nav.todo',
    fallback: 'To-dos',
    instances: todoInstances,
    variants: [
      {
        id: 'todo.open',
        labelKey: 'dashw.todoOpen',
        fallback: 'Offene Aufgaben',
        descKey: 'dashw.todoOpenDesc',
        descFallback: 'Alle offenen Aufgaben, abhakbar',
        min: S(3, 4),
        sizes: { s: S(3, 6), m: S(6, 8), l: S(12, 8) },
        defaultSize: 'm'
      },
      {
        id: 'todo.today',
        labelKey: 'dashw.todoToday',
        fallback: 'Heute fällig',
        descKey: 'dashw.todoTodayDesc',
        descFallback: 'Nur die Aufgaben von heute',
        min: S(3, 3),
        sizes: { s: S(3, 5), m: S(4, 7) },
        defaultSize: 's'
      },
      {
        id: 'todo.count',
        labelKey: 'dashw.todoCount',
        fallback: 'Anzahl offen',
        descKey: 'dashw.todoCountDesc',
        descFallback: 'Nur die Zahl, ganz klein',
        min: S(2, 2),
        sizes: { s: S(3, 3) },
        defaultSize: 's'
      }
    ]
  },
  {
    id: 'shopping',
    feature: 'shopping',
    icon: ShoppingCart,
    labelKey: 'nav.shopping',
    fallback: 'Einkaufsliste',
    instances: shoppingInstances,
    variants: [
      {
        id: 'shopping.list',
        labelKey: 'dashw.shoppingList',
        fallback: 'Einkaufsliste',
        descKey: 'dashw.shoppingListDesc',
        descFallback: 'Offene Artikel, abhakbar',
        min: S(3, 4),
        sizes: { s: S(3, 6), m: S(6, 8), l: S(12, 8) },
        defaultSize: 'm'
      },
      {
        id: 'shopping.count',
        labelKey: 'dashw.shoppingCount',
        fallback: 'Offene Artikel',
        descKey: 'dashw.shoppingCountDesc',
        descFallback: 'Nur die Anzahl',
        min: S(2, 2),
        sizes: { s: S(3, 3) },
        defaultSize: 's'
      }
    ]
  },
  {
    id: 'budget',
    feature: 'budget',
    icon: Wallet,
    labelKey: 'nav.budget',
    fallback: 'Budget',
    variants: [
      {
        id: 'budget.remaining',
        labelKey: 'dashw.budgetRemaining',
        fallback: 'Übrig diesen Monat',
        descKey: 'dashw.budgetRemainingDesc',
        descFallback: 'Was diesen Monat noch übrig ist',
        min: S(2, 2),
        sizes: { s: S(3, 3), m: S(4, 4) },
        defaultSize: 's'
      },
      {
        id: 'budget.expenses',
        labelKey: 'dashw.budgetExpenses',
        fallback: 'Ausgaben',
        descKey: 'dashw.budgetExpensesDesc',
        descFallback: 'Summe der Ausgaben diesen Monat',
        min: S(2, 2),
        sizes: { s: S(3, 3), m: S(4, 4) },
        defaultSize: 's'
      },
      {
        id: 'budget.income',
        labelKey: 'dashw.budgetIncome',
        fallback: 'Einnahmen',
        descKey: 'dashw.budgetIncomeDesc',
        descFallback: 'Summe der Einnahmen diesen Monat',
        min: S(2, 2),
        sizes: { s: S(3, 3), m: S(4, 4) },
        defaultSize: 's'
      },
      {
        id: 'budget.categories',
        labelKey: 'dashw.budgetCategories',
        fallback: 'Top-Kategorien',
        descKey: 'dashw.budgetCategoriesDesc',
        descFallback: 'Wofür das Geld diesen Monat weggeht',
        min: S(3, 4),
        sizes: { m: S(4, 7), l: S(6, 8) },
        defaultSize: 'm'
      },
      {
        id: 'budget.recent',
        labelKey: 'dashw.budgetRecent',
        fallback: 'Letzte Buchungen',
        descKey: 'dashw.budgetRecentDesc',
        descFallback: 'Die neuesten Einnahmen und Ausgaben',
        min: S(3, 4),
        sizes: { m: S(4, 7), l: S(6, 8) },
        defaultSize: 'm'
      }
    ]
  },
  {
    id: 'savings',
    feature: 'savings',
    icon: PiggyBank,
    labelKey: 'nav.savings',
    fallback: 'Sparen',
    instances: savingsInstances,
    variants: [
      {
        id: 'savings.balance',
        labelKey: 'dashw.savingsBalance',
        fallback: 'Kontostand',
        descKey: 'dashw.savingsBalanceDesc',
        descFallback: 'Stand eines Kontos oder aller zusammen',
        min: S(2, 2),
        sizes: { s: S(3, 3), m: S(4, 4) },
        defaultSize: 's'
      },
      {
        id: 'savings.accounts',
        labelKey: 'dashw.savingsAccounts',
        fallback: 'Alle Sparkonten',
        descKey: 'dashw.savingsAccountsDesc',
        descFallback: 'Liste aller Konten mit Stand',
        min: S(3, 3),
        sizes: { m: S(4, 6), l: S(6, 7) },
        defaultSize: 'm'
      }
    ]
  },
  {
    id: 'fitness',
    feature: 'fitness',
    icon: Dumbbell,
    labelKey: 'nav.fitnessOverview',
    fallback: 'Fitness',
    instances: fitnessInstances,
    variants: [
      {
        id: 'fitness.week',
        labelKey: 'dashw.fitnessWeek',
        fallback: 'Workouts diese Woche',
        descKey: 'dashw.fitnessWeekDesc',
        descFallback: 'Anzahl Trainings diese Woche',
        min: S(2, 2),
        sizes: { s: S(3, 3), m: S(4, 4) },
        defaultSize: 's'
      },
      {
        id: 'fitness.recent',
        labelKey: 'dashw.fitnessRecent',
        fallback: 'Letzte Workouts',
        descKey: 'dashw.fitnessRecentDesc',
        descFallback: 'Die zuletzt eingetragenen Trainings',
        min: S(3, 3),
        sizes: { m: S(4, 6), l: S(6, 7) },
        defaultSize: 'm'
      }
    ]
  },
  {
    id: 'timetracker',
    feature: 'timetracker',
    icon: Timer,
    labelKey: 'nav.timetracker',
    fallback: 'Zeiterfassung',
    variants: [
      {
        id: 'timetracker.week',
        labelKey: 'dashw.timeWeek',
        fallback: 'Diese Woche',
        descKey: 'dashw.timeWeekDesc',
        descFallback: 'Erfasste Stunden diese Woche',
        min: S(2, 2),
        sizes: { s: S(3, 3), m: S(4, 4) },
        defaultSize: 's'
      },
      {
        id: 'timetracker.running',
        labelKey: 'dashw.timeRunning',
        fallback: 'Laufender Timer',
        descKey: 'dashw.timeRunningDesc',
        descFallback: 'Zeigt live, was gerade läuft',
        min: S(2, 2),
        sizes: { s: S(3, 3), m: S(4, 4) },
        defaultSize: 's'
      }
    ]
  },
  {
    id: 'diary',
    feature: 'diary',
    icon: BookHeart,
    labelKey: 'nav.diary',
    fallback: 'Tagebuch',
    variants: [
      {
        id: 'diary.recent',
        labelKey: 'dashw.diaryRecent',
        fallback: 'Letzte Einträge',
        descKey: 'dashw.diaryRecentDesc',
        descFallback: 'Die neuesten Tagebucheinträge',
        min: S(3, 3),
        sizes: { m: S(4, 6), l: S(6, 7) },
        defaultSize: 'm'
      }
    ]
  },
  {
    id: 'fuel',
    feature: 'fuel',
    icon: Fuel,
    labelKey: 'nav.fuel',
    fallback: 'Tankbuch',
    variants: [
      {
        id: 'fuel.consumption',
        labelKey: 'dashw.fuelConsumption',
        fallback: 'Verbrauch',
        descKey: 'dashw.fuelConsumptionDesc',
        descFallback: 'Verbrauch und letzter Tankstopp',
        min: S(2, 2),
        sizes: { s: S(3, 3), m: S(4, 4) },
        defaultSize: 's'
      }
    ]
  },
  {
    id: 'tutoring',
    feature: 'tutoring',
    icon: GraduationCap,
    labelKey: 'nav.tutoring',
    fallback: 'Nachhilfe',
    variants: [
      {
        id: 'tutoring.open',
        labelKey: 'dashw.tutoringOpen',
        fallback: 'Offene Zahlungen',
        descKey: 'dashw.tutoringOpenDesc',
        descFallback: 'Was Schüler noch schulden',
        min: S(2, 2),
        sizes: { s: S(3, 3), m: S(4, 4) },
        defaultSize: 's'
      }
    ]
  },
  {
    id: 'projects',
    feature: 'projects',
    icon: FolderKanban,
    labelKey: 'nav.projects',
    fallback: 'Projekte',
    variants: [
      {
        id: 'projects.overview',
        labelKey: 'dashw.projectsOverview',
        fallback: 'Übersicht',
        descKey: 'dashw.projectsOverviewDesc',
        descFallback: 'Gewinn bzw. Stunden je Projekt',
        min: S(3, 3),
        sizes: { s: S(3, 3), m: S(4, 6) },
        defaultSize: 'm'
      }
    ]
  }
];

// ---- lookups ---------------------------------------------------------------
const VARIANTS_BY_ID = {};
const CATEGORY_BY_VARIANT = {};
for (const cat of CATEGORIES) {
  for (const v of cat.variants) {
    VARIANTS_BY_ID[v.id] = v;
    CATEGORY_BY_VARIANT[v.id] = cat;
  }
}

export const getVariant = (id) => VARIANTS_BY_ID[id] || null;
export const getCategoryForVariant = (id) => CATEGORY_BY_VARIANT[id] || null;

export const isFeatureEnabled = (data, featureId) =>
  data?.settings?.enabledFeatures?.[featureId] !== false;

// A category is offerable when its feature is on in Settings — or, where it
// defines its own `available` predicate, when that says so.
export const categoryAvailable = (category, data) =>
  category?.available
    ? category.available(data)
    : isFeatureEnabled(data, category?.feature);

// Categories the user can actually pick from right now. Hiding a feature
// hides its widgets everywhere.
export const availableCategories = (data) =>
  CATEGORIES.filter((c) => categoryAvailable(c, data));

// Can this stored widget still render? Its category must be available and,
// when the category is instance-based, the instance it points at must still
// exist (calendar left/deleted, group removed, personal calendar hidden…).
// Widgets stay in storage while hidden, so re-enabling brings them back.
export const isWidgetVisible = (widget, data) => {
  if (!widget) return false;
  const cat = getCategoryForVariant(widget.variant);
  if (!getVariant(widget.variant) || !cat) return false;
  if (!categoryAvailable(cat, data)) return false;
  if (!cat.instances || widget.scope == null) return true;
  return resolveInstances(cat, data).some((i) => i.id === widget.scope);
};

export const resolveInstances = (category, data) =>
  category.instances ? category.instances(data) : [];

// Label for a widget's data instance, e.g. the shared calendar's name.
export const instanceLabel = (variantId, scope, data, t) => {
  const cat = getCategoryForVariant(variantId);
  if (!cat || !cat.instances) return null;
  const found = resolveInstances(cat, data).find((i) => i.id === scope);
  if (!found) return null;
  return found.label || t(found.labelKey, found.fallback);
};

// On a phone every widget is full width, so only the height matters — use
// the smallest size the variant offers. Without this a stat tile inherited
// the desktop height and showed one number in a half-screen-tall box.
export const mobileHeight = (variant) => {
  if (!variant || !variant.sizes) return 6;
  const size = variant.sizes.s || variant.sizes.m || variant.sizes.l;
  return (size && size.h) || 6;
};

export const newWidgetId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return `w-${crypto.randomUUID()}`;
  } catch {
    /* fall through */
  }
  return `w-${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 6)}`;
};
